import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Trash, Upload } from "@phosphor-icons/react";
import Layout from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function CompanySettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/company/settings`, {
        withCredentials: true,
      });
      setSettings(data);
    } catch (err) {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }

    if (file.size > 500 * 1024) {
      toast.error("Imagem muito grande. Máximo 500KB");
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        try {
          await axios.post(
            `${API}/company/logo`,
            { logo: base64 },
            { withCredentials: true },
          );
          toast.success("Logo atualizada com sucesso!");
          setSettings((prev) => ({ ...prev, logo: base64 }));
        } catch (err) {
          toast.error(err.response?.data?.detail || "Erro ao enviar logo");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false);
      toast.error("Erro ao processar imagem");
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.post(
        `${API}/company/logo`,
        { logo: "" },
        { withCredentials: true },
      );
      toast.success("Logo removida");
      setSettings((prev) => ({ ...prev, logo: null }));
    } catch (err) {
      toast.error("Erro ao remover logo");
    }
  };

  if (loading) {
    return (
      <Layout title="Configurações" showBack backTo="/dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Configurações" showBack backTo="/dashboard">
      <div className="space-y-6 pb-4">
        {/* Company Info */}
        <div className="card p-5 sm:p-6">
          <h2 className="text-lg font-bold mb-4">Dados da Empresa</h2>
          <div className="space-y-4 sm:space-y-3 text-sm">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-[#A1A1AA] font-medium">Nome:</span>
              <span className="font-bold sm:font-normal">{settings?.name}</span>
            </div>
            {settings?.cnpj && (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-[#A1A1AA] font-medium">CNPJ:</span>
                <span>{settings.cnpj}</span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-[#A1A1AA] font-medium">Email:</span>
              <span className="break-all">{settings?.email}</span>
            </div>
            {settings?.license_expiry && (
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-[#A1A1AA] font-medium">Licença até:</span>
                <span>
                  {new Date(settings.license_expiry).toLocaleDateString(
                    "pt-BR",
                  )}
                </span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:justify-between gap-1 pt-2 border-t border-white/10">
              <span className="text-[#A1A1AA] font-medium">Status:</span>
              <span
                className={`font-bold ${settings?.active ? "text-[#00E676]" : "text-red-500"}`}
              >
                {settings?.active ? "Ativa" : "Inativa"}
              </span>
            </div>
          </div>
        </div>

        {/* Logo Upload */}
        <div className="card p-5 sm:p-6">
          <h2 className="text-lg font-bold mb-4">Logo da Empresa</h2>
          <div className="flex flex-col items-center gap-4">
            {settings?.logo ? (
              <div className="relative">
                <img
                  src={settings.logo}
                  alt="Logo"
                  className="w-32 h-32 object-contain rounded-lg bg-white p-2 shadow-lg"
                  data-testid="company-logo-preview"
                />
                <button
                  onClick={handleRemoveLogo}
                  className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                  data-testid="remove-logo-btn"
                >
                  <Trash size={16} className="text-white" />
                </button>
              </div>
            ) : (
              <div className="w-32 h-32 rounded-lg bg-[#1A1A1A] border-2 border-dashed border-white/20 flex flex-col items-center justify-center">
                <Camera size={32} className="text-[#A1A1AA] mb-1" />
                <span className="text-xs text-[#A1A1AA]">Sem logo</span>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="logo-file-input"
            />

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2 px-6"
              data-testid="upload-logo-btn"
            >
              <Upload size={18} />
              {uploading ? "Enviando..." : "Enviar Logo"}
            </button>
            <p className="text-xs text-[#A1A1AA] text-center">
              PNG, JPG ou SVG. Máximo 500KB.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
